import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "../ui/dropdown-menu";
import { DropdownMenuTrigger } from "@radix-ui/react-dropdown-menu";
import { Fragment } from "react";
import { ChevronDown } from "lucide-react";
import { Selectable } from "@erp/shared-libs/common";
import { cn } from "@erp/shared-libs/utils";

interface SearchMenuProps {
  searchOptions: Selectable[];
  searchOptionId: string;
  /**
   * 觸發鈕外觀
   * - default：藥丸下拉鈕（既有樣式）
   * - ai：扁平 pill 搭配的 13px 緊湊下拉鈕
   */
  variant?: "default" | "ai";
  /**
   * 排在搜尋框裡的位置
   * - leading：最左邊那一顆（左側圓角，既有樣式）
   * - inner：接在另一顆選單後面（沒有圓角，左邊一條分隔線）
   */
  position?: "leading" | "inner";
  /**
   * 觸發鈕的無障礙名稱（畫面上只顯示選到的選項，讀不出這顆選單是選什麼的時候用）
   */
  ariaLabel?: string;
  onSearchOptionIdChange: (searchTypeId: string) => void;
}

export const SearchMenu = ({
  searchOptions,
  searchOptionId,
  variant = "default",
  position = "leading",
  ariaLabel,
  onSearchOptionIdChange,
}: SearchMenuProps) => {
  // =================================================
  // Variables
  // =================================================
  /**
   * 是否為 ai pill 外觀（13px 緊湊字 + 次要灰字）
   */
  const isAi = variant === "ai";

  /**
   * 是否接在另一顆選單後面
   */
  const isInner = position === "inner";

  // =================================================
  // Render
  // =================================================
  return (
    <DropdownMenu>
      {/* Trigger */}
      <DropdownMenuTrigger asChild>
        <Button
          variant="dropdown"
          size="dropdown"
          aria-label={ariaLabel}
          className={cn(
            "rounded-r-none border-none",
            isAi && "text-muted-foreground-darker text-dense rounded-l-xl",
            // 接在另一顆選單後面：兩邊都不要圓角，內距收窄，左邊用一條短的分隔線隔開
            isInner &&
              `before:bg-border relative rounded-none px-3 before:absolute before:inset-y-2.5 before:left-0
              before:w-px`
          )}
        >
          <div className="flex items-center justify-center">
            <p className="text-nowrap">
              {searchOptions.find((x) => x.id === searchOptionId)?.label}
            </p>
            <ChevronDown
              className={cn(
                "dark:text-white",
                isInner ? "ml-1.5" : "ml-4",
                isAi && "text-muted-foreground"
              )}
            />
          </div>
        </Button>
      </DropdownMenuTrigger>
      {/* Content */}
      <DropdownMenuContent className={cn(isAi && "border-border-lighter")}>
        {searchOptions?.map((option, index) => (
          <Fragment key={option.id}>
            <DropdownMenuItem
              onClick={() => onSearchOptionIdChange(option.id as string)}
              className="items-center justify-center"
            >
              {option.label}
            </DropdownMenuItem>

            {index < searchOptions.length - 1 ? <DropdownMenuSeparator /> : null}
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
